import {
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import CommonChip from "Components/UI/Tables/CommonChip";
import useTableOptions from "Hooks/useTableOptions";
import {
  getParseStatusColorByLabel,
  getSystemStatusColorByLabel,
} from "utils/colors";
import { useCallback, useEffect, useMemo, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import ToggleSelectBtn from "Components/UI/Tables/ToggeSelectBtn";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";
import {
  abortImport,
  changeAgreementChain,
  confirmImport,
  deleteReferences,
  getInvoicesByClientId,
  getInvoicesGroupedByClientId,
  ignoreWarnings,
} from "API/swesum";
import { formatPrice } from "utils/priceformatter";
import {
  getDefaultServiceLabelById,
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import { LoadingButton } from "@mui/lab";
import { DEFAULT_SERVICE } from "utils/options/DEFAULT_SERVICE";
import ConfirmModal from "./ConfirmModal";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const createTableRows = (invoices) => {
  return invoices.map((i) => {
    return [
      i.Id,
      i.InvoiceDate, // Fakturadatum
      i.CalcFfdDate, // Förfallodatum
      i.Type, // Typ
      { invoiceNo: i.ExternalInvoiceNo, invoiceId: i.Id }, // Fakturanummer
      i.OCR, // OCR
      getDefaultServiceLabelById(i.Service), // Tjänst
      formatPrice(
        i.Price === null ? 0 : i.Type === "Credit" ? i.Price * -1 : i.Price
      ), // Belopp
      {
        endCustomerId: i.EndCustomerId,
        endCustomerNumber: i.EndCustomer.ExternalCustomerNr,
      }, // Kundnummer
      { customerName: i.EndCustomer.Name, newCustomer: i.NewCustomer }, // Kundnamn

      formatPrice(i.TotalDueAmount === null ? 0 : i.TotalDueAmount), // Förfallet saldo

      { status: i.ParseStatus, ignoreWarnings: i.IgnoreWarnings }, // Kontrollstatus
      cutStrAtAndAddDots(i.ParseMessage, 45), // Meddelande
      getSystemStatusLabelById(i.SystemStatus), // Status
      i.CreateTime, // Skapad
    ];
  });
};

const getInvoiceType = (type) => {
  switch (type) {
    case "Credit":
      return "Kredit";
    case "Invoice":
      return "Faktura";
    default:
      return null;
  }
};

const ProcessInvoices = () => {
  const { handleColumnShow, isColDisplayed } =
    useTableOptions("InvoicesMainTable");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(0);
  const [clients, setClients] = useState([]);
  const [disableCheckboxes, setDisableCheckboxes] = useState(true);
  const [dropdownValue, setDropdownValue] = useState("");
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [rowCount, setRowCount] = useState(30);
  const [invoices, setInvoices] = useState([]);
  const [openCleanRefCreditDialogOpen, setOpenCleanRefCreditDialogOpen] =
    useState(false);
  const [openRemoveInvoiceDialog, setOpenRemoveInvoiceDialog] = useState(false);
  const { openSuccess, openError } = usePopupNotificationContext();

  const [openConfirmWarningModal, setOpenConfirmWarningModal] = useState(false);
  const [openConfirmChangeServiceModal, setOpenConfirmChangeServiceModal] =
    useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(-1);

  const checkAll = (force = false) => {
    if (!selectedClient) {
      return;
    }

    if (!force && selectedInvoiceIds.size === invoices.length) {
      setSelectedInvoiceIds(new Set());
      return;
    }
    const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
    for (const invoice of invoices) {
      if (selectedInvoiceIds.has(invoice.Id)) {
        continue;
      }
      newSelectedInvoiceIds.add(invoice.Id);
    }
    setSelectedInvoiceIds(newSelectedInvoiceIds);
  };

  const handleCheckBoxChange = useCallback(
    (value) => {
      const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
      if (selectedInvoiceIds.has(value)) {
        newSelectedInvoiceIds.delete(value);
      } else {
        newSelectedInvoiceIds.add(value);
      }
      setSelectedInvoiceIds(newSelectedInvoiceIds);
    },
    [selectedInvoiceIds]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();

        setCurrentRowIndex((prevIndex) => {
          const newIndex =
            event.key === "ArrowUp"
              ? Math.max(prevIndex - 1, 0)
              : prevIndex < rows.length - 1
              ? prevIndex + 1
              : prevIndex;
          return newIndex;
        });
      } else if (
        event.key === "Enter" &&
        currentRowIndex > -1 &&
        currentRowIndex < rows.length
      ) {
        if (rows[currentRowIndex]) {
          handleCheckBoxChange(rows[currentRowIndex][0]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentRowIndex, rows, handleCheckBoxChange]);

  const isItemSelected = (value) => {
    return selectedInvoiceIds.has(value);
  };

  const checkColumnDisplay = (colIndex) => {
    const col = columns[colIndex];
    if (col) {
      return isColDisplayed(col.name);
    }
  };

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        customHeadRender: (columnMeta) => (
          <th
            key={columnMeta.index}
            style={{ borderBottom: "1px solid lightgray" }}
          >
            <Checkbox
              checked={selectedInvoiceIds.size === invoices.length}
              onClick={() => checkAll()}
            />
          </th>
        ),
        customBodyRender: (value) => {
          return (
            <tr
              key={value}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Checkbox
                checked={isItemSelected(value)}
                onChange={() => handleCheckBoxChange(value)}
              />
            </tr>
          );
        },
      },
    },
    {
      name: "Fakturadatum",
    },
    {
      name: "Förfallodatum",
    },
    {
      name: "Typ",
      options: {
        display: isColDisplayed("Typ"),
        customBodyRender: (value) => {
          if (value) {
            return (
              <Chip
                color={`${value === "Credit" ? "error" : "default"}`}
                label={getInvoiceType(value)}
              />
            );
          }
          return null;
        },
      },
    },
    {
      name: "Fakturanummer",
      options: {
        display: isColDisplayed("Fakturanummer"),
        empty: false,
        customBodyRender: (value) => {
          return (
            <Link to={`/invoices/${value.invoiceId}`}>{value.invoiceNo}</Link>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const val1 = parseInt(obj1.data.invoiceNo, 10);
            const val2 = parseInt(obj2.data.invoiceNo, 10);
            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
      },
    },
    {
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
      },
    },
    {
      name: "Tjänst",
      options: {
        display: isColDisplayed("Tjänst"),
      },
    },
    {
      name: "Belopp",
      options: {
        display: isColDisplayed("Belopp"),
        customBodyRender: (value) => {
          const number = Number(value.split(" ").join(""));
          return (
            <span style={{ color: number < 0 ? "red" : null }}>{value}</span>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const val1 = parseInt(
              obj1.data.replace(" ", "").replace(",", "."),
              10
            );
            const val2 = parseInt(
              obj2.data.replace(" ", "").replace(",", "."),
              10
            );

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
      },
    },
    {
      name: "Kundnummer",
      options: {
        display: isColDisplayed("Kundnummer"),
        customBodyRender: (value) => {
          return (
            <Link to={`/endcustomers/${value.endCustomerId}`}>
              {value.endCustomerNumber}
            </Link>
          );
        },
      },
    },
    {
      name: "Kundnamn",
      options: {
        display: isColDisplayed("Kundnamn"),
        customBodyRender: (value) => {
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                minWidth: "150px",
              }}
            >
              {value.newCustomer ? (
                <Tooltip title="Ny kund">
                  <InfoIcon fontSize="small" color="info" />
                </Tooltip>
              ) : null}
              {value.customerName}
            </span>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const val1 = obj1.data.customerName;
            const val2 = obj2.data.customerName;
            if (order === "asc") {
              return val1.localeCompare(val2);
            } else {
              return val2.localeCompare(val1);
            }
          };
        },
      },
    },

    {
      name: "Förfallet saldo",
    },

    {
      name: "Kontroll",
      options: {
        display: isColDisplayed("Kontroll"),
        sort: true,
        customBodyRender: (value) => {
          const statusLabel = getParseStatusLabelById(value.status);
          return (
            <CommonChip
              labelName={statusLabel}
              color={getParseStatusColorByLabel(
                statusLabel,
                value.ignoreWarnings
              )}
            />
          );
        },
      },
    },
    {
      name: "Meddelande",
      options: {
        display: isColDisplayed("Meddelande"),
      },
    },
    {
      name: "Status",
      options: {
        display: isColDisplayed("Status"),
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "CreateTime",
      label: "Skapad",
      options: {
        display: isColDisplayed("CreateTime"),
      },
    },
  ];

  const options = {
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    customToolbar: () => {
      return (
        <>
          <ToggleSelectBtn
            onClick={() => setDisableCheckboxes(!disableCheckboxes)}
          />
          <RefreshBtn />
        </>
      );
    },
    rowsPerPage: rowCount,
    rowsPerPageOptions: [10, 30, 100],
    selectableRowsHeader: true,
    onChangeRowsPerPage: (numberOfRows) => setRowCount(numberOfRows),
    selectableRowsHideCheckboxes: disableCheckboxes,
    textLabels: {
      body: {
        noMatch: loading ? <CircularProgress /> : "",
      },
    },
    filter: false,
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
    customRowRender: (data, _, rowIndex) => {
      return (
        <TableRow
          style={{
            background:
              currentRowIndex === rowIndex ? "rgba(25, 118, 210, 0.08)" : null,
          }}
          key={rowIndex}
          onClick={(event) => {
            event.stopPropagation();
            setCurrentRowIndex(rowIndex);
          }}
        >
          {data.map((cell, cellIndex) => {
            if (checkColumnDisplay(cellIndex)) {
              return <TableCell key={cellIndex}>{cell}</TableCell>;
            }
            return null;
          })}
        </TableRow>
      );
    },
  };

  const handleOnConfirm = async () => {
    setLoading(true);
    const invoiceIds = Array.from(selectedInvoiceIds);
    const res = await confirmImport(invoiceIds);
    setSelectedInvoiceIds(new Set());
    if (res.Status === "Ok") {
      fetchInvoices();
      openSuccess(res.Message, 3000);
    } else {
      openError(res.Message, 3000);
    }
    setLoading(false);
  };

  const fetchInvoices = async () => {
    const res = await getInvoicesGroupedByClientId();
    setClients(res);
    if (res.length > 0) {
      if (!res.some((client) => client.ClientId === selectedClientId)) {
        setSelectedClientId(res[0].ClientId);
      }
    } else {
      setSelectedClientId(null);
    }
  };

  const sortByParseStatus = (rows) => {
    rows.sort((a, b) => {
      if (a.ParseStatus === b.ParseStatus) {
        return Number(a.ExternalInvoiceNo) - Number(b.ExternalInvoiceNo);
      }
      const parseOrder = { 1: 1, 2: 2, 0: 3 };
      return parseOrder[a.ParseStatus] - parseOrder[b.ParseStatus];
    });

    return rows;
  };

  const selectedClient = useMemo(() => {
    return clients.find((client) => client.ClientId === selectedClientId);
  }, [clients, selectedClientId]);

  const asyncFetchInvoices = async (clientId) => {
    setLoading(true);
    setInvoices([]); // To clear the list before fetching new so it doesnt show wrong info
    const options = {
      Filters: [
        {
          Column: "SystemStatus",
          Value: "0",
          Operation: 0,
          Or: false,
        },
      ],
      Order: {
        Column: "ParseStatus",
        Order: 1,
      },
      Page: 0,
      PageSize: 0,
    };

    try {
      const res = await getInvoicesByClientId(clientId, options);
      setInvoices(res.Rows);
    } catch (err) {
      openError("Någonting gick fel vid hämtning", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedInvoiceIds(new Set());
    if (selectedClientId) {
      asyncFetchInvoices(selectedClientId);
    } else {
      setRows([]);
    }
  }, [selectedClientId, clients]);

  useEffect(() => {
    const rows = createTableRows(invoices);
    setRows(rows);
    checkAll(true);
  }, [invoices]);

  const handleIgnoreWarninigs = async () => {
    setModalLoading(true);
    const invoiceIds = Array.from(selectedInvoiceIds);
    const res = await ignoreWarnings(invoiceIds);
    if (res.Status === "ok") {
      setOpenConfirmWarningModal(false);
      fetchInvoices();
      openSuccess(res.Message, 3000);
    } else {
      openError(res.Message, 3000);
    }
    setModalLoading(false);
  };

  const handleChangeService = async () => {
    setModalLoading(true);
    const invoiceIds = Array.from(selectedInvoiceIds);
    const res = await changeAgreementChain(dropdownValue, invoiceIds);

    if (res.Status === "ok") {
      setOpenConfirmChangeServiceModal(false);
      fetchInvoices();
      openSuccess(res.Message, 3000);
    } else {
      openError(res.Message, 3000);
    }
    setModalLoading(false);
  };

  const onCloseInvoices = async () => {
    setModalLoading(true);
    try {
      const invoiceIds = Array.from(selectedInvoiceIds);
      const res = await abortImport(invoiceIds);
      if (res.Status === "ok") {
        fetchInvoices();
        openSuccess(res.Message, 3000);
      } else {
        openError(res.Message, 3000);
      }
    } catch (err) {
      openError("Någonting gick fel", 3000);
    } finally {
      setModalLoading(false);
      setOpenRemoveInvoiceDialog(false);
    }
  };

  const onCleanRefCredit = async () => {
    setModalLoading(true);
    try {
      const invoiceIds = Array.from(selectedInvoiceIds);
      const res = await deleteReferences(invoiceIds);
      if (res.Status === "ok") {
        setOpenCleanRefCreditDialogOpen(false);
        fetchInvoices();
        openSuccess(res.Message, 3000);
      } else {
        openError(res.Message, 3000);
      }
    } catch (err) {
      openError("Någonting gick fel", 3000);
    } finally {
      setModalLoading(false);
      setOpenCleanRefCreditDialogOpen(false);
    }
  };

  const handleDropdownChange = (e) => {
    setDropdownValue(e.target.value);
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        margin: "20px",
        gap: "20px",
      }}
    >
      <Grid item container spacing={2}>
        <Grid item>
          <FormControl sx={{ width: "20rem" }}>
            <InputLabel id="client-selector">Client</InputLabel>
            <Select
              id="client-selector1"
              value={selectedClientId}
              label="Client"
              sx={{ background: "white" }}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              {clients.map((client) => (
                <MenuItem key={client.ClientId} value={client.ClientId}>
                  {`${client.ClientNumber} - ${client.ClientName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {selectedClient ? (
          <Grid item>
            <Paper
              elevation={4}
              style={{
                width: "20rem",
                padding: "10px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  style={{ fontWeight: 600 }}
                >{`Totalt (${selectedClient.NumberOfInvoices} st):`}</span>
                <span>{`${selectedClient.InvoiceSum} kr`}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{`Finansierat (${selectedClient.PurchaseCount} st):`}</span>{" "}
                <span>{`${selectedClient.PurchaseSum} kr`}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{`Service (${selectedClient.ServiceCount} st):`}</span>
                <span>{`${selectedClient.ServiceSum} kr`}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{`Krediter (${selectedClient.CreditCount} st):`}</span>
                <span>{`${selectedClient.CreditSum} kr`}</span>
              </div>
            </Paper>
          </Grid>
        ) : null}
      </Grid>
      <MUITable
        options={options}
        title={
          <>
            <h2 style={{ color: "lightblack" }}>Fakturor att importera</h2>
            {selectedClient ? (
              <span>{`(${selectedInvoiceIds.size} av ${invoices.length} markerade)`}</span>
            ) : null}
          </>
        }
        data={rows}
        columns={columns}
      />
      {invoices.length > 0 ? (
        <Grid item container flex={1} spacing={3} direction="row">
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel>Välj tjänst</InputLabel>
              <Select
                size="small"
                value={dropdownValue}
                label="Välj tjänst"
                onChange={handleDropdownChange}
                sx={{ background: "white" }}
              >
                {DEFAULT_SERVICE.map((service) => (
                  <MenuItem value={service.id}>{service.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <LoadingButton
              fullWidth
              loading={modalLoading}
              disabled={
                !dropdownValue || selectedInvoiceIds.size === 0 || loading
              }
              onClick={() => setOpenConfirmChangeServiceModal(true)}
              variant="contained"
            >
              Byt
            </LoadingButton>
          </Grid>
        </Grid>
      ) : null}
      <Grid item container>
        <Grid item container spacing={2}>
          <Grid item>
            <LoadingButton
              disabled={selectedInvoiceIds.size === 0 || loading}
              fullWidth
              loading={modalLoading}
              variant="contained"
              onClick={() => setOpenConfirmWarningModal(true)}
            >
              Ignorera varningar
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton
              disabled={selectedInvoiceIds.size === 0 || loading}
              fullWidth
              loading={modalLoading}
              variant="contained"
              onClick={() => setOpenCleanRefCreditDialogOpen(true)}
            >
              Rensa ref kredit
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container justifyContent={"space-between"}>
        <Grid item>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={rows.length === 0 || selectedInvoiceIds.size === 0}
            onClick={() => setOpenRemoveInvoiceDialog(true)}
            color="error"
          >
            Ta bort
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={rows.length === 0 || selectedInvoiceIds.size === 0}
            onClick={handleOnConfirm}
          >
            Bokför och skriv ut
          </LoadingButton>
        </Grid>
      </Grid>
      <Dialog
        open={openCleanRefCreditDialogOpen}
        onClose={() => setOpenCleanRefCreditDialogOpen(false)}
      >
        <DialogTitle>Rensa ref kredit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vill du rensa fakturareferens för markerade kreditar?
          </DialogContentText>
          <DialogActions>
            <LoadingButton
              variant="contained"
              onClick={onCleanRefCredit}
              autoFocus
              loading={modalLoading}
            >
              Rensa
            </LoadingButton>
            <LoadingButton
              onClick={() => setOpenCleanRefCreditDialogOpen(false)}
            >
              Avbryt
            </LoadingButton>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openRemoveInvoiceDialog}
        onClose={() => setOpenRemoveInvoiceDialog(false)}
      >
        <DialogTitle>Avsluta markerade fakturor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vill du avsluta markerade fakturor?
          </DialogContentText>
          <DialogActions>
            <LoadingButton
              variant="contained"
              onClick={onCloseInvoices}
              color="error"
              loading={modalLoading}
            >
              Avsluta
            </LoadingButton>
            <LoadingButton onClick={() => setOpenRemoveInvoiceDialog(false)}>
              Avbryt
            </LoadingButton>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <ConfirmModal
        open={openConfirmChangeServiceModal}
        onClose={() => setOpenConfirmChangeServiceModal(false)}
        title={"Byta tjänst"}
      >
        {`Är du säker på att du vill byta tjänst till ${
          DEFAULT_SERVICE.find((ds) => ds.id === dropdownValue)?.Name
        }`}
        <div
          style={{
            display: "flex",
            marginTop: "50px",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <LoadingButton
            loading={modalLoading}
            onClick={() => handleChangeService()}
            variant="contained"
          >
            Byt
          </LoadingButton>
          <LoadingButton
            onClick={() => setOpenConfirmChangeServiceModal(false)}
          >
            Avbryt
          </LoadingButton>
        </div>
      </ConfirmModal>
      <ConfirmModal
        open={openConfirmWarningModal}
        onClose={() => setOpenConfirmWarningModal(false)}
        title={"Ignorera varningar"}
      >
        {`Är du säker på att du vill ignorera varningar?`}
        <div
          style={{
            display: "flex",
            marginTop: "50px",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <LoadingButton
            onClick={() => handleIgnoreWarninigs()}
            loading={modalLoading}
            variant="contained"
          >
            Ignorera varningar
          </LoadingButton>
          <LoadingButton onClick={() => setOpenConfirmWarningModal(false)}>
            Avbryt
          </LoadingButton>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default ProcessInvoices;
